import { type RefObject, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Green ringed parrot in the Yare mask's filigree style: a translucent lime Fresnel shell with
// crease-line filigree, hidden lines removed by a depth-only pre-pass. The model carries two
// clips: `intro` (condenses out of fog and lands on the perch; the approach is baked into the
// root bones) and `loop` (10.3 s perched, seamless). Built from the Sketchfab capture by
// 3d-mask/sketchfab-scrape/xray/build_final.sh (meshopt geometry + animation, mask as WebP).
const MODEL_URL = '/assets/models/parrot-filigree.glb';
const YAW = 0.6; // three-quarter turn; a parrot reads poorly head-on
const CAMERA_DISTANCE = 6;
const FOV = 32;
// Framing: the perch sits just above the bottom anchor (apply button), centred over it; the bird
// (head to perch) fills BIRD_FILL of the space between the top anchor (hero title) and the perch,
// leaving air under the title. The tail hangs below the perch, behind the button. Without anchors,
// fall back to fractions of the canvas height.
const POLE_GAP = 0.065; // of canvas height, above the bottom anchor (the slanted perch dips on one end)
const BIRD_FILL = 0.72;
const LIFT = 0.05; // of canvas height: nudges the whole bird (head and perch) up
const MAX_WIDTH = 1.4; // of canvas width, measured on the (generous, tail-inclusive) perched box: narrow screens shrink the bird
// When the width caps the size (phones), the bird no longer fills the gap between title and
// button; this share of the leftover height goes under the perch, lifting the bird so the
// empty space splits between above the head and below the perch instead of all sitting on top.
const SLACK_BELOW = 0.5;
const FALLBACK_TOP = 0.22;
const FALLBACK_POLE = 0.78;
// Drag to rotate (enabled once the bird has landed): horizontal drag spins it about the perch with
// inertia; vertical drag (mouse only, touch keeps page scroll) tilts it a little and springs back.
const DRAG_YAW = 0.008; // rad per px
const DRAG_PITCH = 0.004; // rad per px
const MAX_PITCH = 0.3;
const SPIN_DAMPING = 3.5; // 1/s
const PITCH_RETURN = 2.5; // 1/s
const APPEAR_TIME = 1.1; // s for the bird to condense out of the fog
const POLE_FADE = 1.4; // s for the perch's soft fade-in (done before touchdown at ~1.6 s)
const CREASE_DEG = 14;
const LINE_ALPHA = 0.238; // each segment draws twice (see createLines): 1 - (1 - 0.238)^2 = 0.42

type Appear = { value: number };

// "Out of the fog": a drifting noise field decides which parts condense first. The perch
// (uFogNoise = 0) fades evenly instead.
const FOG_GLSL = `
  uniform float uAppear;
  uniform float uFogNoise;
  uniform float uTime;
  float fogIn(vec3 p) {
    if (uAppear >= 1.0) return 1.0;
    if (uFogNoise <= 0.0) return uAppear;
    float n = 0.5 + 0.5 * sin(p.x * 6.0 + sin(p.y * 4.3 + uTime * 0.8) * 2.2) * sin(p.z * 5.1 + p.y * 3.7 - uTime * 0.6);
    return smoothstep(n * 0.55, n * 0.55 + 0.45, uAppear);
  }`;

function fogUniforms(appear: Appear, noise: boolean, time: { value: number }) {
  return { uAppear: appear, uFogNoise: { value: noise ? 1 : 0 }, uTime: time };
}

function createSurfaceMaterial(mask: THREE.Texture | null, appear: Appear, noise: boolean, time: { value: number }, occluder: boolean) {
  return new THREE.ShaderMaterial({
    uniforms: { uMask: { value: mask }, ...fogUniforms(appear, noise, time) },
    defines: { ...(mask ? { USE_CUTOUT: '' } : {}), ...(occluder ? { OCCLUDER: '' } : {}) },
    vertexShader: `
      #include <common>
      #include <skinning_pars_vertex>
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      varying vec2 vUv;
      void main() {
        #include <beginnormal_vertex>
        #include <skinbase_vertex>
        #include <skinnormal_vertex>
        #include <begin_vertex>
        #include <skinning_vertex>
        vec4 viewPosition = modelViewMatrix * vec4(transformed, 1.0);
        vNormal = normalize(normalMatrix * objectNormal);
        vView = normalize(-viewPosition.xyz);
        vWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
        vUv = uv;
        gl_Position = projectionMatrix * viewPosition;
      }`,
    fragmentShader: `
      uniform sampler2D uMask;
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      varying vec2 vUv;
      ${FOG_GLSL}
      void main() {
        #ifdef USE_CUTOUT
          vec4 m = texture2D(uMask, vUv);
          if (min(m.a, m.g) < 0.28) discard;  // feather-card cutout (mask stored as greyscale)
        #endif
        float appear = fogIn(vWorld);
        #ifdef OCCLUDER
          if (appear < 0.5) discard;  // a half-condensed bird does not hide what is behind it yet
        #else
          if (appear <= 0.002) discard;
        #endif
        float rim = pow(1.0 - abs(dot(normalize(vNormal), normalize(vView))), 2.2);
        vec3 color = vec3(0.07, 0.085, 0.008) + rim * vec3(0.55, 0.66, 0.04);
        gl_FragColor = vec4(color * appear, (0.12 + rim * 0.43) * appear);
      }`,
    transparent: !occluder,
    depthWrite: occluder,
    colorWrite: !occluder,
    side: THREE.DoubleSide,
    // the occluder only writes depth, pushed back slightly so lines lying on the surface still pass
    polygonOffset: occluder,
    polygonOffsetFactor: occluder ? 1 : 0,
    polygonOffsetUnits: occluder ? 2 : 0,
  });
}

// Crease + boundary edges (the THREE.EdgesGeometry rule) as pairs of source vertex indices, so
// the lines can reuse the mesh's position and skinning buffers. Vertices are welded by position
// first (cards and UV seams split them) so edge lookups use numeric keys.
function creaseEdges(geometry: THREE.BufferGeometry, thresholdDeg: number) {
  const pos = geometry.getAttribute('position');
  const index = geometry.getIndex();
  if (!index) return [];
  const weld = new Map<string, number>();
  const welded = new Uint32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const key = `${Math.round(pos.getX(i) * 1e5)},${Math.round(pos.getY(i) * 1e5)},${Math.round(pos.getZ(i) * 1e5)}`;
    let id = weld.get(key);
    if (id === undefined) {
      id = weld.size;
      weld.set(key, id);
    }
    welded[i] = id;
  }
  const stride = weld.size;
  const cosT = Math.cos(THREE.MathUtils.degToRad(thresholdDeg));
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const open = new Map<number, { i0: number; i1: number; nx: number; ny: number; nz: number }>();
  const out: number[] = [];
  for (let f = 0; f < index.count; f += 3) {
    const v = [index.getX(f), index.getX(f + 1), index.getX(f + 2)];
    a.fromBufferAttribute(pos, v[0]);
    b.fromBufferAttribute(pos, v[1]);
    c.fromBufferAttribute(pos, v[2]);
    c.sub(b);
    a.sub(b);
    const n = c.cross(a).normalize();
    for (let e = 0; e < 3; e++) {
      const i0 = v[e];
      const i1 = v[(e + 1) % 3];
      const w0 = welded[i0];
      const w1 = welded[i1];
      if (w0 === w1) continue;
      const key = w0 < w1 ? w0 * stride + w1 : w1 * stride + w0;
      const prev = open.get(key);
      if (prev) {
        if (prev.nx * n.x + prev.ny * n.y + prev.nz * n.z <= cosT) out.push(prev.i0, prev.i1);
        open.delete(key);
      } else {
        open.set(key, { i0, i1, nx: n.x, ny: n.y, nz: n.z });
      }
    }
  }
  for (const e of open.values()) out.push(e.i0, e.i1);
  return out;
}

// Sibling drawn with the mesh's world transform; skinned (sharing its skeleton) when the mesh is.
function attachSibling(mesh: THREE.Mesh, geometry: THREE.BufferGeometry, material: THREE.Material) {
  const skinned = mesh as THREE.SkinnedMesh;
  const obj = skinned.isSkinnedMesh ? new THREE.SkinnedMesh(geometry, material) : new THREE.Mesh(geometry, material);
  if (skinned.isSkinnedMesh) (obj as THREE.SkinnedMesh).bind(skinned.skeleton, skinned.bindMatrix);
  obj.frustumCulled = false;
  mesh.add(obj);
  return obj;
}

// Filigree lines. LineSegments cannot be skinned, so each edge (a, b) becomes the degenerate
// triangle (a, b, b) drawn in wireframe on a SkinnedMesh (which draws a-b twice).
function createLines(mesh: THREE.Mesh, appear: Appear, noise: boolean, time: { value: number }) {
  const pairs = creaseEdges(mesh.geometry, CREASE_DEG);
  const tri = new Uint32Array((pairs.length / 2) * 3);
  for (let i = 0, j = 0; i < pairs.length; i += 2) {
    tri[j++] = pairs[i];
    tri[j++] = pairs[i + 1];
    tri[j++] = pairs[i + 1];
  }
  const geometry = new THREE.BufferGeometry();
  for (const name of ['position', 'skinIndex', 'skinWeight']) {
    const attribute = mesh.geometry.getAttribute(name);
    if (attribute) geometry.setAttribute(name, attribute);
  }
  geometry.setIndex(new THREE.BufferAttribute(tri, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: fogUniforms(appear, noise, time),
    vertexShader: `
      #include <common>
      #include <skinning_pars_vertex>
      varying vec3 vWorld;
      void main() {
        #include <skinbase_vertex>
        #include <begin_vertex>
        #include <skinning_vertex>
        vec4 world = modelMatrix * vec4(transformed, 1.0);
        vWorld = world.xyz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }`,
    fragmentShader: `
      varying vec3 vWorld;
      ${FOG_GLSL}
      void main() {
        gl_FragColor = vec4(0.9, 1.0, 0.09, ${LINE_ALPHA} * fogIn(vWorld));
      }`,
    wireframe: true,
    transparent: true,
    depthWrite: false,
  });
  const lines = attachSibling(mesh, geometry, material);
  lines.renderOrder = 1;
  return lines;
}

type Props = {
  topAnchor?: RefObject<HTMLElement | null>;
  bottomAnchor?: RefObject<HTMLElement | null>;
};

export function ParrotFiligree({ topAnchor, bottomAnchor }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.45;
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 30);
    const modelGroup = new THREE.Group();
    scene.add(modelGroup);
    const clock = new THREE.Clock();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const time = { value: 0 };
    const birdAppear: Appear = { value: reducedMotion ? 1 : 0 };
    const poleAppear: Appear = { value: reducedMotion ? 1 : 0 };
    const owned: Array<{ dispose(): void }> = [];
    let model: THREE.Object3D | null = null;
    let mixer: THREE.AnimationMixer | null = null;
    let intro: THREE.AnimationAction | null = null;
    // perched-pose landmarks in model space (unscaled), measured once after load
    let marks: { head: number; pole: number; cx: number; cz: number; span: number } | null = null;
    let startedAt = 0;
    let frame = 0;
    let disposed = false;
    let paused = false;
    let inView = true;
    // drag-to-rotate state
    const canvas = renderer.domElement;
    canvas.style.touchAction = 'pan-y'; // vertical swipes keep scrolling the page
    let interactive = false;
    let dragging = false;
    let pointerId = -1;
    let lastX = 0;
    let lastY = 0;
    let lastMove = 0;
    let userYaw = 0;
    let userPitch = 0;
    let spin = 0;
    const enableRotation = () => {
      interactive = true;
      canvas.style.cursor = 'grab';
    };

    // Solve scale + vertical offset so the head and the perch land on their target screen rows.
    // Rotation about the vertical axis (yaw, sway) leaves heights unchanged, so two rows pin it.
    const layout = () => {
      const height = mount.clientHeight;
      if (!model || !marks || !height) return;
      const box = mount.getBoundingClientRect();
      const top = topAnchor?.current?.getBoundingClientRect();
      const bottom = bottomAnchor?.current?.getBoundingClientRect();
      const topPx = top ? top.bottom - box.top : FALLBACK_TOP * height;
      const anchorPx = bottom ? bottom.top - box.top - POLE_GAP * height : FALLBACK_POLE * height;
      const polePx = anchorPx - LIFT * height; // lift is a pure translation: size is set by the anchors
      const headPx = polePx - BIRD_FILL * (anchorPx - topPx);
      const unitsPerPx = (2 * CAMERA_DISTANCE * Math.tan(THREE.MathUtils.degToRad(FOV / 2))) / height;
      const worldY = (px: number) => (height / 2 - px) * unitsPerPx;
      const widthUnits = mount.clientWidth * unitsPerPx;
      const fillScale = (worldY(headPx) - worldY(polePx)) / (marks.head - marks.pole);
      const scale = Math.min(fillScale, (MAX_WIDTH * widthUnits) / marks.span);
      if (!(scale > 0)) return;
      const slackPx = ((fillScale - scale) * (marks.head - marks.pole)) / unitsPerPx;
      model.scale.setScalar(scale);
      model.position.set(-marks.cx * scale, worldY(polePx - SLACK_BELOW * slackPx) - marks.pole * scale, -marks.cz * scale);
    };

    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      if (!width || !height) return;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.position.set(0, 0, CAMERA_DISTANCE);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      layout();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    for (const anchor of [topAnchor?.current, bottomAnchor?.current]) if (anchor) observer.observe(anchor);
    resize();

    const render = () => {
      frame = window.requestAnimationFrame(render);
      if (paused) return;
      const dt = Math.min(clock.getDelta(), 0.1);
      if (model && !reducedMotion) {
        const since = clock.elapsedTime - startedAt;
        mixer?.update(dt);
        time.value = since;
        if (intro?.isRunning()) {
          const a = Math.min(1, intro.time / APPEAR_TIME);
          birdAppear.value = a * a * (3 - 2 * a);
        } else {
          birdAppear.value = 1;
        }
        const p = Math.min(1, since / POLE_FADE);
        poleAppear.value = p * p * (3 - 2 * p);
      }
      if (model) {
        if (!dragging) {
          userYaw += spin * dt;
          spin *= Math.exp(-SPIN_DAMPING * dt);
          userPitch *= Math.exp(-PITCH_RETURN * dt);
        }
        const sway = reducedMotion ? 0 : Math.sin(clock.elapsedTime * 0.34) * 0.12;
        modelGroup.rotation.set(userPitch, YAW + userYaw + sway, 0);
      }
      renderer.render(scene, camera);
    };
    render();

    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    loader.load(
      MODEL_URL,
      (gltf) => {
        if (disposed) return;
        model = gltf.scene;
        const introClip = gltf.animations.find((clip) => clip.name === 'intro');
        const loopClip = gltf.animations.find((clip) => clip.name === 'loop');
        mixer = new THREE.AnimationMixer(model);
        const loop = loopClip ? mixer.clipAction(loopClip) : null;
        // landmarks of the perched pose (loop start): head top and the perch centre
        loop?.play();
        mixer.setTime(0);
        model.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(model, true);
        // pivot on the perch: it stays centred (over the button) and the bird turns about it
        const pole = box.getCenter(new THREE.Vector3());
        model.traverse((node) => {
          const mesh = node as THREE.Mesh;
          if (mesh.isMesh && !(mesh as THREE.SkinnedMesh).isSkinnedMesh) new THREE.Box3().setFromObject(mesh).getCenter(pole);
        });
        // on-screen width of the perched bird at the three-quarter yaw (box corners about the perch)
        let minX = Infinity;
        let maxX = -Infinity;
        for (const x of [box.min.x, box.max.x])
          for (const z of [box.min.z, box.max.z]) {
            const rx = (x - pole.x) * Math.cos(YAW) + (z - pole.z) * Math.sin(YAW);
            minX = Math.min(minX, rx);
            maxX = Math.max(maxX, rx);
          }
        marks = { head: box.max.y, pole: pole.y, cx: pole.x, cz: pole.z, span: maxX - minX };
        modelGroup.add(model);
        layout();

        const meshes: THREE.Mesh[] = [];
        model.traverse((node) => {
          if ((node as THREE.Mesh).isMesh) meshes.push(node as THREE.Mesh);
        });
        for (const mesh of meshes) {
          mesh.frustumCulled = false;
          const source = mesh.material as THREE.MeshStandardMaterial;
          const mask = source.alphaTest > 0 ? source.map : null;
          source.dispose();
          const isPole = !(mesh as THREE.SkinnedMesh).isSkinnedMesh;
          const appear = isPole ? poleAppear : birdAppear;
          const surface = createSurfaceMaterial(mask, appear, !isPole, time, false);
          const depth = createSurfaceMaterial(mask, appear, !isPole, time, true);
          mesh.material = surface;
          attachSibling(mesh, mesh.geometry, depth).renderOrder = -1;
          const lines = createLines(mesh, appear, !isPole, time);
          // geometries (incl. the line/occluder children) are disposed by the model traversal
          owned.push(surface, depth, lines.material as THREE.Material);
          if (mask) owned.push(mask);
        }

        if (introClip && loop && !reducedMotion) {
          intro = mixer.clipAction(introClip);
          intro.setLoop(THREE.LoopOnce, 1);
          intro.clampWhenFinished = true;
          mixer.addEventListener('finished', (event) => {
            if (event.action !== intro) return;
            intro.stop();
            loop.reset().play(); // the intro ends exactly on the loop's first pose
            enableRotation();
          });
          loop.stop();
          intro.play();
        } else {
          enableRotation();
        }
        startedAt = clock.elapsedTime;
      },
      undefined,
      () => {
        if (!disposed) setFailed(true);
      },
    );

    const onVisibility = () => {
      paused = document.hidden || !inView;
      if (!paused) clock.getDelta();
    };
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        onVisibility();
      },
      { threshold: 0.01 },
    );
    visibilityObserver.observe(mount);
    document.addEventListener('visibilitychange', onVisibility);

    const onPointerDown = (event: PointerEvent) => {
      if (!interactive || (event.pointerType === 'mouse' && event.button !== 0)) return;
      dragging = true;
      pointerId = event.pointerId;
      canvas.setPointerCapture(pointerId);
      lastX = event.clientX;
      lastY = event.clientY;
      lastMove = performance.now();
      spin = 0;
      canvas.style.cursor = 'grabbing';
    };
    const onPointerMove = (event: PointerEvent) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const now = performance.now();
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      userYaw += dx * DRAG_YAW;
      const instant = (dx * DRAG_YAW) / Math.max(0.008, (now - lastMove) / 1000);
      spin = spin * 0.6 + instant * 0.4; // smoothed release velocity
      if (event.pointerType === 'mouse') {
        userPitch = THREE.MathUtils.clamp(userPitch + dy * DRAG_PITCH, -MAX_PITCH, MAX_PITCH);
      }
      lastX = event.clientX;
      lastY = event.clientY;
      lastMove = now;
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false;
      if (canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId);
      if (performance.now() - lastMove > 80) spin = 0; // held still before letting go: no fling
      canvas.style.cursor = 'grab';
    };
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      visibilityObserver.disconnect();
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      mixer?.stopAllAction();
      model?.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh && mesh.geometry) mesh.geometry.dispose();
      });
      for (const item of owned) item.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="absolute inset-0" aria-label="Loro de collar verde en 3D">
      <div ref={mountRef} className="absolute inset-0" />
      {failed && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-xs uppercase tracking-[0.2em] text-primary/70">
          No se pudo cargar el loro
        </div>
      )}
    </div>
  );
}
